import { createTheme } from "@mui/material/styles";
import { purple, orange, yellow } from "@mui/material/colors";

export const colorPrimary = {
  light: "#F7F9FA",
  main: "#0D5876",
  mainLight: "#e6f3f7",
  mainHover: "rgba(13, 88, 118, 0.7)",
  mainSelected: "rgba(13, 88, 118, 1)",
  dark: "#707070",
  darker: "#333333",
  contrastText: "#FFF",
  disabled: "#999",
  brandPrimary: "#127DA9",
  brandPrimaryHover: "rgba(18, 125, 169, 0.7)",
  brandPrimarySelected: "rgba(18, 125, 169, 0.1)",
  lightBlue: {
    primary: "#7DDAFC",
    link: "#00AFF0",
    selected: "rgba(125, 218, 252, 0.5)",
    background: "rgba(125, 218, 252, 0.5)",
  },
  green: {
    dark: "#49B581",
    light: "#DFFFEE",
  },
  red: {
    light: "#FFAFA2",
  },
};

export const colorSecondary = {
  main: "#DEDEDE",
  light: "#DDDDDD",
  dark: "#00000029",
  contrastText: "#666666",
};

const defaultTheme = createTheme();

export const theme = createTheme({
  palette: {
    primary: colorPrimary,
    secondary: colorSecondary,
    orange: {
      main: "#c2721c",
      light: "#fdf0e3",
    },
    error: {
      main: "#943637",
    },
    success: {
      main: "#32855D",
    },
    warning: {
      main: orange[500],
    },
    publish: purple,
    approved: "#32855D",
    inactive: "#666666",
    takedown: orange[500],
    pending: yellow[700],
    rejected: "#B0220C",
    deleted: "#B0220C",
    failed: "#B0220C",
    background: {
      default: "#F2F7F9",
    },
    brandPrimary: colorPrimary.brandPrimary,
    label: "rgba(0, 0, 0, 0.54)",
  },

  spacing: 5,

  typography: {
    fontFamily: ["Nunito", "sans-serif"].join(","),
    fontWeightRegular: 400,
    fontWeightBold: 700,
    fontWeightLight: 300,
    fontWeightMedium: 600,
  },

  breakpoints: {
    values: {
      xs: 420,
      sm: 720,
      creatorAdminDashboardMd: 1000,
      mid: 1200,
      md: 1440,
      lg: 1640,
      xl: 1780,
      full: window.innerWidth,
    },
  },

  components: {
    // v4 `props` → v5 `defaultProps`
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
    },
    // Preserve v4 default input variant (v5 defaults to "outlined")
    MuiTextField: {
      defaultProps: {
        variant: "standard",
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: "standard",
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: "62.5%",
        },
        body: {
          fontSize: "1.6rem",
          lineHeight: "1.6rem",
        },
        a: {
          color: colorPrimary.darker,
          textDecoration: "none",
          fontWeight: 800,
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          "&.Mui-expanded": {
            minHeight: 50,
          },
        },

        content: {
          marginTop: 0,
          marginBottom: 0,
          "&.Mui-expanded": {
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: "1.6rem",
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.15)",
          fontSize: "1.6rem",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          height: "40px",
          fontSize: "1.6rem",
          fontWeight: 600,
        },

        contained: {
          height: "40px",
          width: "120px",
          backgroundColor: colorPrimary.main,
          color: colorPrimary.contrastText,
          borderRadius: "1.5em",
          textTransform: "uppercase",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: colorPrimary.mainHover,
            color: "white",
            border: 0,
            boxShadow: "none",
          },
        },

        containedPrimary: {
          backgroundColor: colorPrimary.brandPrimary,
          fontWeight: 700,
          fontSize: "1.4rem",
          "&:hover": {
            backgroundColor: colorPrimary.brandPrimaryHover,
            color: "white",
            border: 0,
          },

          outlined: {
            borderRadius: "20px",
          },
        },

        outlinedPrimary: {
          color: colorPrimary.brandPrimary,
          borderWidth: 2,
          borderColor: colorPrimary.brandPrimary,
          borderRadius: 30,
          textTransform: "none",
          fontSize: "1.4rem",
          lineHeight: "20px",
          fontWeight: 700,
          height: "36px",
          "&:hover": {
            borderWidth: 2,
            borderColor: colorPrimary.brandPrimary,
          },
        },

        text: {
          color: colorPrimary.brandPrimary,
          textTransform: "none",
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colorPrimary.darker,
          "&.Mui-checked,&.Mui-indeterminate": {
            color: "black",
          },
        },

        colorPrimary: {
          backgroundColor: "transparent",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          "&.Mui-checked": {
            color: colorPrimary.darker,
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "1.6rem",
          margin: 5,
        },

        label: {
          paddingLeft: 15,
          paddingRight: 15,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        // Guard the fixed width/radius on `fullScreen` so they don't clobber
        // MUI's `paperFullScreen` styles (v5 applies theme styleOverrides after
        // the component's own variants, so unconditional values would win).
        paper: ({ ownerState }) => ({
          boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.15)",
          ...(!ownerState.fullScreen && {
            width: "866px",
            borderRadius: "20px",
          }),
        }),

        paperWidthSm: ({ ownerState }) => ({
          ...(!ownerState.fullScreen && {
            maxWidth: 680,
          }),
        }),
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "8px 24px 30px 24px",
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: colorPrimary.brandPrimary,
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "1.6rem",
          lineHeight: "3.4rem",
          position: "absolute",
          top: "4rem",
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          fontSize: "1.6rem",
        },

        colorPrimary: {
          backgroundColor: colorPrimary.main,
          borderRadius: "50px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
          height: "36px",
          width: "36px",
          "&:hover": {
            backgroundColor: colorPrimary.mainHover,
          },
        },
      },
    },

    MuiInput: {
      styleOverrides: {
        underline: {
          "&::after": {
            borderColor: colorPrimary.brandPrimary,
          },
          "&.Mui-disabled::before": {
            borderBottomStyle: "solid",
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: "1.6rem",
          lineHeight: "3.4rem",
        },

        input: {
          lineHeight: "3.4rem",
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "1.6rem",
        },

        // Guard on variant: v5 injects theme styleOverrides at a higher
        // priority than built-in variant styles, so an unguarded `shrink`
        // clobbers the outlined label's notch transform. Keep this tweak for
        // standard/filled only and let outlined keep its default transform.
        shrink: ({ ownerState }) =>
          ownerState.variant !== "outlined"
            ? { transform: "translate(0, 1.5px) scale(0.85)" }
            : {},
      },
    },

    MuiLink: {
      defaultProps: {
        underline: "hover",
      },
      styleOverrides: {
        root: {
          color: colorPrimary.darker,
        },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected, &.Mui-selected:hover": {
            color: "black",
          },
        },

        button: {
          transition: "none",
        },

        dense: {
          paddingLeft: defaultTheme.spacing(3),
          paddingRight: defaultTheme.spacing(1.5),
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 0,
          paddingRight: defaultTheme.spacing(2),
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        root: {
          fontSize: "1.6rem",
          fontWeight: "normal",
          whiteSpace: "pre-wrap",
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow:
            "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
          borderRadius: 10,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "1.6rem",
          // v5 MenuItem inherits ButtonBase instead of ListItem, so the
          // MuiListItem `Mui-selected` override above no longer reaches it.
          "&.Mui-selected, &.Mui-selected:hover": {
            color: "black",
          },
          // justifyContent: "space-between",
          // transition: "none",
          "&:hover": {
            // backgroundColor: colorPrimary.main,
            backgroundColor: colorSecondary.light,
            // color: "white",
            color: "black",
            "& p": {
              // color: "white",
              color: "black",
            },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
            // borderColor: `${colorPrimary.brandPrimary} !important`,
          },
        },

        input: {
          height: "1.1876em",
        },

        notchedOutline: {
          // borderColor: "transparent",
          // borderColor: `${colorPrimary.brandPrimary} !important`,
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          "&.Mui-checked": {
            color: colorPrimary.brandPrimary,
          },
        },
      },
    },

    MuiSelect: {
      defaultProps: {
        variant: "standard",
      },
      styleOverrides: {
        icon: {
          top: "calc(50% - 6px)",
        },

        select: {
          padding: "0 24px 0 0",
        },
      },
    },

    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: "1.6rem",
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          height: "50px",
          width: "80px",
          display: "flex",
        },

        colorPrimary: {
          "&.Mui-checked": {
            color: "white",
          },
        },

        switchBase: {
          top: "8px",
          left: "10px",
          "&.Mui-checked": {
            transform: "translateX(26px)",
            color: "white",
            "& + .MuiSwitch-track": {
              opacity: "1",
            },
          },
        },

        thumb: {
          height: "16px",
          width: "16px",
        },

        track: {
          borderRadius: "50px",
          "&.Mui-checked": {
            backgroundColor: colorPrimary.main,
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "2rem",
          lineHeight: "1.8rem",
          fontStyle: "normal",
          fontWeight: "600",
          textTransform: "none",
          color: colorPrimary.brandPrimary,
          "&.Mui-selected": {
            fontWeight: "700",
            fontSize: "2rem",
            lineHeight: "2.8rem",
            color: colorPrimary.darker,
          },
        },

        textColorPrimary: {
          color: colorPrimary.brandPrimary,
          "&.Mui-selected": {
            fontWeight: "700",
            fontSize: "2rem",
            lineHeight: "2.8rem",
            color: colorPrimary.darker,
          },
        },

        labelIcon: {
          fontSize: "1.6rem",
        },
      },
    },

    MuiTabPanel: {
      styleOverrides: {
        root: {
          borderTop: "1px solid #CCC",
          padding: "0",
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colorPrimary.brandPrimary,
          height: "4px",
          borderRadius: "20px",
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        body: {
          fontSize: "1.6rem",
          padding: "7.5px 5px",
        },

        stickyHeader: {
          height: "40px",
          // padding: 0,
          padding: "7.5px 5px",
          maxWidth: 150,
          backgroundColor: "white",
          fontSize: "1.6rem",
          fontWeight: "bold",
        },

        paddingCheckbox: {
          padding: 0,
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          padding: "0 30px",
        },
      },
    },

    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: colorPrimary.darker,
          backgroundColor: "white",
          borderRadius: "20px",
        },

        displayedRows: {
          fontSize: "1.6rem",
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1.6rem",
        },
      },
    },

    MuiTreeItem: {
      styleOverrides: {
        root: {
          fontWeight: "inherit",
        },

        // x-tree-view v7 applies `.Mui-selected` to the content element.
        content: {
          paddingLeft: defaultTheme.spacing(1),
          borderRadius: 50,
          "&:hover": {
            backgroundColor: colorSecondary.light,
            borderRadius: 50,
          },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: colorPrimary.lightBlue.selected,
            borderRadius: 50,
          },
          "&.Mui-selected .MuiTreeItem-label": {
            backgroundColor: colorPrimary.lightBlue.selected,
            fontWeight: 700,
          },
        },

        label: {
          padding: defaultTheme.spacing(1.5, 0),
          backgroundColor: "transparent !important",
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: "3rem",
          fontWeight: 300,
        },
        h2: {
          fontSize: "1.75rem",
          lineHeight: "1.5em",
        },
        h3: {
          // fontSize: "1.5rem",
          fontSize: "1.6rem",
          lineHeight: "1.8em",
        },
        h4: {
          fontSize: "1.8rem",
          fontWeight: "bold",
          color: colorPrimary.main,
          lineHeight: "3.4rem",
        },
        h5: {
          fontSize: "1.6rem",
          fontWeight: "bold",
          color: colorPrimary.darker,
          lineHeight: "2.6rem",
        },
        //---- Media Titles
        h6: {
          fontSize: "2rem",
          fontWeight: "bold",
          lineHeight: "2.6rem",
        },
        subtitle1: {
          fontSize: "1.6rem",
          fontWeight: 700,
        },
        //---- Media Subtitles
        subtitle2: {
          fontSize: "1.6rem",
          fontWeight: "normal",
          color: colorPrimary.darker,
          lineHeight: "1.8rem",
        },
        body1: {
          // fontSize: "1.4rem",
          fontSize: "1.6rem",
          fontWeight: "normal",
        },
        body2: {
          // fontSize: "1.4rem",
          fontSize: "1.6rem",
          color: colorPrimary.darker,
        },
      },
    },
  },
});
